import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { sendEmailVerification } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const COOLDOWN_SECONDS = 60;

function EmailIconLarge({ className }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

export default function VerifyEmail() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (user.emailVerified) {
      navigate('/', { replace: true });
      return;
    }
  }, [user, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const handleHoVerificato = async () => {
    if (!user) return;
    setError('');
    setChecking(true);
    try {
      await user.reload();
      if (auth.currentUser?.emailVerified) {
        navigate('/', { replace: true });
        return;
      }
      setError('Email non ancora verificata. Controlla la tua casella di posta.');
    } catch (err) {
      setError(err.message || 'Errore durante il controllo.');
    } finally {
      setChecking(false);
    }
  };

  const handleInviaDiNuovo = async () => {
    if (!user || cooldown > 0) return;
    setError('');
    setResendLoading(true);
    try {
      await sendEmailVerification(user);
      setCooldown(COOLDOWN_SECONDS);
    } catch (err) {
      setError(err.message || 'Errore nell\'invio dell\'email.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  if (!user) return null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-4 py-10">
      <Card className="w-full max-w-md text-center">
        <CardContent className="space-y-4 p-6 text-center">
          <EmailIconLarge className="mx-auto mb-1 h-16 w-16 text-brand-sky" />
          <h1 className="font-display text-2xl font-semibold tracking-wide text-brand-ice">
            Verifica la tua email
          </h1>
          <p className="text-left text-sm leading-relaxed text-brand-ice/90 sm:text-center">
            Abbiamo inviato un link di verifica a{' '}
            <span className="font-semibold text-brand-sky">{user.email}</span>. Clicca il link nella mail per accedere a Matadel.
          </p>
          <div className="flex items-start justify-center gap-2 text-left text-sm text-brand-ice/75 sm:items-center sm:text-center">
            <span className="shrink-0" aria-hidden>
              ⚠️
            </span>
            <span>Non trovi la mail? Controlla la cartella Spam o Posta indesiderata.</span>
          </div>
          {error && (
            <div className="rounded-btn border border-loss/60 bg-loss/15 px-3 py-2 text-left text-sm text-red-100 sm:text-center">
              {error}
            </div>
          )}
          <div className="flex flex-col gap-2 pt-1">
            <Button
              type="button"
              size="lg"
              className="w-full"
              onClick={handleHoVerificato}
              disabled={checking}
            >
              {checking ? 'Controllo in corso...' : 'Ho verificato'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="w-full"
              onClick={handleInviaDiNuovo}
              disabled={resendLoading || cooldown > 0}
            >
              {resendLoading
                ? 'Invio in corso...'
                : cooldown > 0
                  ? `Puoi reinviare tra ${cooldown} secondi`
                  : 'Invia di nuovo'}
            </Button>
            <button
              type="button"
              onClick={handleLogout}
              className="mt-1 text-sm font-medium text-brand-sky underline-offset-4 hover:underline"
            >
              Esci
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
